import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, CheckCircle, Eraser, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignaturePad = ({ onSave, label }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#d1d5db';
    ctx.beginPath();
    ctx.moveTo(20, canvas.height - 25);
    ctx.lineTo(canvas.width - 20, canvas.height - 25);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#1a1a1a';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };
  const startDraw = (e) => { e.preventDefault(); setIsDrawing(true); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
  const endDraw = () => setIsDrawing(false);
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setLineDash([4, 4]); ctx.strokeStyle = '#d1d5db'; ctx.beginPath();
    ctx.moveTo(20, canvas.height - 25); ctx.lineTo(canvas.width - 20, canvas.height - 25); ctx.stroke();
    ctx.setLineDash([]); ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 2.5;
    setHasDrawn(false);
  };
  const getData = useCallback(() => hasDrawn ? canvasRef.current.toDataURL('image/png') : null, [hasDrawn]);
  useEffect(() => { onSave && onSave(getData); }, [getData, onSave]);

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="border-2 border-dashed rounded-lg bg-white relative touch-none">
        <canvas ref={canvasRef} className="w-full cursor-crosshair"
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
        <span className="absolute bottom-1 left-4 text-xs text-muted-foreground pointer-events-none">Sign above the line</span>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="text-muted-foreground text-xs">
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
};

const YouthApplicationPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const getAthleteSigRef = useRef(null);
  const getGuardianSigRef = useRef(null);

  const [form, setForm] = useState({
    athlete_name: '', date_of_birth: '', age: '', mailing_address: '', city: '', province: '', postal_code: '', phone: '', email: '',
    guardian_name: '', guardian_relationship: '', guardian_phone: '', guardian_phone_secondary: '', guardian_email: '',
    guardian_address: '', guardian_city: '', guardian_province: '', guardian_postal_code: '',
    subscription_name: '', subscription_email: '',
    rodeo_circuits: '', planned_events_list: '', primary_events: [],
    shirt_size: '', hat_size: '', apparel_notes: '',
    confirm_age: false, confirm_membership: false, confirm_events: false, confirm_waiver: false, confirm_accompany: false, confirm_accurate: false,
  });

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const toggle = (field) => () => setForm(p => ({ ...p, [field]: !p[field] }));
  const toggleEvent = (ev) => setForm(p => ({
    ...p, primary_events: p.primary_events.includes(ev) ? p.primary_events.filter(e => e !== ev) : [...p.primary_events, ev]
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const athleteSig = getAthleteSigRef.current?.();
    const guardianSig = getGuardianSigRef.current?.();

    if (!athleteSig || !guardianSig) {
      toast.error('Both athlete and parent/guardian signatures are required.');
      return;
    }
    if (!form.confirm_age || !form.confirm_membership || !form.confirm_waiver || !form.confirm_accompany || !form.confirm_accurate) {
      toast.error('Please confirm all required checkboxes.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/youth-applications`, {
        ...form,
        signature_athlete: athleteSig,
        signature_guardian: guardianSig,
      });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="p-8 sm:p-12">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="font-display font-bold text-2xl uppercase mb-3">Application Submitted!</h1>
            <p className="text-muted-foreground mb-6">Thank you for applying for the Elevated Roping Media Youth Sponsorship Program. Selected athletes will be contacted directly.</p>
            <Link to="/youth-athletes">
              <Button className="rounded-full">Back to Youth Athletes</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/youth-athletes" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Youth Athletes
        </Link>

        <div className="mb-8">
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">Youth Sponsorship Draw Application</h1>
          <p className="text-muted-foreground mt-2">Please complete all sections below. Incomplete applications will not be entered into the draw.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* 1. Athlete Information */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">1. Athlete Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name of Youth *</Label><Input required value={form.athlete_name} onChange={set('athlete_name')} data-testid="athlete-name" /></div>
                <div><Label>Date of Birth *</Label><Input required type="date" value={form.date_of_birth} onChange={set('date_of_birth')} data-testid="athlete-dob" /></div>
              </div>
              <div><Label>Age (must be 17 or under)</Label><Input value={form.age} onChange={set('age')} placeholder="e.g. 15" data-testid="athlete-age" /></div>
              <div><Label>Mailing Address *</Label><Input required value={form.mailing_address} onChange={set('mailing_address')} /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>City *</Label><Input required value={form.city} onChange={set('city')} /></div>
                <div><Label>Province *</Label><Input required value={form.province} onChange={set('province')} /></div>
                <div><Label>Postal Code *</Label><Input required value={form.postal_code} onChange={set('postal_code')} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone Number *</Label><Input required value={form.phone} onChange={set('phone')} /></div>
                <div><Label>Email Address *</Label><Input required type="email" value={form.email} onChange={set('email')} data-testid="athlete-email" /></div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none mt-2">
                <input type="checkbox" checked={form.confirm_age} onChange={toggle('confirm_age')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm">I confirm that the applicant is 17 years of age or younger. Proof of age (birth certificate) may be requested at event check-in.</span>
              </label>
            </CardContent>
          </Card>

          {/* 2. Parent/Guardian */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">2. Parent / Guardian Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Legal Name *</Label><Input required value={form.guardian_name} onChange={set('guardian_name')} data-testid="guardian-name" /></div>
                <div><Label>Relationship to Athlete *</Label><Input required value={form.guardian_relationship} onChange={set('guardian_relationship')} placeholder="e.g. Mother, Father" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Primary Phone *</Label><Input required value={form.guardian_phone} onChange={set('guardian_phone')} /></div>
                <div><Label>Secondary Phone</Label><Input value={form.guardian_phone_secondary} onChange={set('guardian_phone_secondary')} /></div>
              </div>
              <div><Label>Email Address *</Label><Input required type="email" value={form.guardian_email} onChange={set('guardian_email')} data-testid="guardian-email" /></div>
              <div><Label>Mailing Address (if different from athlete)</Label><Input value={form.guardian_address} onChange={set('guardian_address')} /></div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>City</Label><Input value={form.guardian_city} onChange={set('guardian_city')} /></div>
                <div><Label>Province</Label><Input value={form.guardian_province} onChange={set('guardian_province')} /></div>
                <div><Label>Postal Code</Label><Input value={form.guardian_postal_code} onChange={set('guardian_postal_code')} /></div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 space-y-2 text-sm">
                <p className="font-semibold">By signing below, I confirm that:</p>
                <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                  <li>I am the legal parent or guardian of the applicant</li>
                  <li>All information provided is accurate</li>
                  <li>A signed liability waiver is required before participation</li>
                  <li>I agree to transport and accompany my child to the designated event</li>
                  <li>My child is representing Elevated Roping Media Inc. and its sponsors</li>
                  <li>I agree to media use including photos and video for promotional purposes</li>
                  <li>Failure to meet program expectations may result in removal</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 3. Membership */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">3. Membership Requirement</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.confirm_membership} onChange={toggle('confirm_membership')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm">I confirm that I or a direct family member holds an active subscription to the Elevated Roping Media platform.</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Subscription Name (if different from athlete)</Label><Input value={form.subscription_name} onChange={set('subscription_name')} /></div>
                <div><Label>Email used for subscription</Label><Input value={form.subscription_email} onChange={set('subscription_email')} /></div>
              </div>
            </CardContent>
          </Card>

          {/* 4. Rodeo Participation */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">4. Rodeo Participation Commitment</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.confirm_events} onChange={toggle('confirm_events')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm">I confirm that the athlete plans to enter a minimum of 12 rodeo or jackpot events during the upcoming season.</span>
              </label>
              <div><Label>Rodeo circuits, associations, or jackpot series you plan to compete in</Label><Textarea value={form.rodeo_circuits} onChange={set('rodeo_circuits')} rows={3} placeholder="e.g. CCA, CPRA, local jackpots..." /></div>
              <div><Label>List a minimum of 12 events you intend to enter (name and location)</Label><Textarea value={form.planned_events_list} onChange={set('planned_events_list')} rows={5} placeholder="1. Event name - Location&#10;2. ..." /></div>
              <div>
                <Label>Primary Event(s) Competing In</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Team Roping – Header', 'Team Roping – Heeler'].map(ev => (
                    <button key={ev} type="button" onClick={() => toggleEvent(ev)}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors ${form.primary_events.includes(ev) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'}`}>
                      {ev}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 5. Apparel */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">5. Apparel Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Shirt Size (Cowboy Shirt)</Label><Input value={form.shirt_size} onChange={set('shirt_size')} placeholder="e.g. Youth L, Adult S" /></div>
                <div><Label>Hat Size (Head Measurement)</Label><Input value={form.hat_size} onChange={set('hat_size')} placeholder="e.g. 7 1/8" /></div>
              </div>
              <div><Label>Additional Notes</Label><Input value={form.apparel_notes} onChange={set('apparel_notes')} /></div>
            </CardContent>
          </Card>

          {/* 6. Waiver */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">6. Waiver & Parental Consent</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.confirm_waiver} onChange={toggle('confirm_waiver')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm">I understand that a signed parent/guardian waiver is mandatory.</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.confirm_accompany} onChange={toggle('confirm_accompany')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm">I confirm that a parent or guardian will attend and accompany the athlete to the roping event.</span>
              </label>
            </CardContent>
          </Card>

          {/* 7. Signatures */}
          <Card>
            <CardHeader><CardTitle className="font-display text-lg uppercase">7. Signatures</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={form.confirm_accurate} onChange={toggle('confirm_accurate')} className="mt-1 w-4 h-4 accent-primary" />
                <span className="text-sm font-medium">I certify that the information provided is accurate and complete.</span>
              </label>
              <SignaturePad label="Athlete Signature *" onSave={(fn) => { getAthleteSigRef.current = fn; }} />
              <SignaturePad label="Parent/Guardian Signature *" onSave={(fn) => { getGuardianSigRef.current = fn; }} />
            </CardContent>
          </Card>

          <Button type="submit" size="lg" disabled={submitting} className="w-full rounded-full py-6 font-bold uppercase tracking-wider" data-testid="submit-application-btn">
            {submitting ? 'Submitting...' : <><Send className="w-5 h-5 mr-2" /> Submit Application</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default YouthApplicationPage;
